import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, MapPin, Users, ArrowUpRight, Bell, Share2, Video, Navigation, Mic, Sparkles, Linkedin, CheckCircle2, Loader2, QrCode, Clock, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useDirectoryItem } from '@/hooks/use-directory-item';
import { useApi } from '@/hooks/use-api';
import { FitScoreCard } from '@/components/fit/FitScoreBadge';
import { toast } from 'sonner';
import { formatEventWhen, parseAppDate } from '@/lib/datetime';
import { useCountdown, effectiveEndMs } from '@/lib/schedule';
import { ShareEvent } from '@/components/community/ShareEvent';

const JoinCountdownNote = ({ start }) => {
  const { status, label } = useCountdown(start, null);
  if (status !== 'soon' && status !== 'upcoming') return null;
  return (
    <p className="text-xs text-muted-foreground text-center inline-flex items-center justify-center gap-1.5 w-full -mt-1" data-testid="event-join-countdown">
      <Video className="h-3.5 w-3.5" /> Join opens 15 min before start · <span className="tabular-nums font-semibold text-foreground">{label}</span>
    </p>
  );
};
import { DetailSkeleton, DetailNotFound } from '@/components/DetailStates';
import { CopilotPrepCard } from '@/components/ai/CopilotPrepCard';
import RichHtml from '@/components/ui/RichHtml';

const DEFAULT_COVER = 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80';

const mapsUrl = (e) => {
  const q = [e.location_name, e.location_address, e.location_city].filter(Boolean).join(', ') || e.location;
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
};

// Relative day label for the hero badge (Live · Today / Tomorrow / etc.)
const dayBadge = (startAt) => {
  if (!startAt) return null;
  const s = parseAppDate(startAt);
  if (!s) return null;
  const now = new Date();
  const days = Math.round((new Date(s.getFullYear(), s.getMonth(), s.getDate()) - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
  if (days === 0) return { live: true, label: 'Live · Today' };
  if (days === 1) return { live: false, label: 'Tomorrow' };
  if (days > 1 && days <= 14) return { live: false, label: `In ${days} days` };
  return null;
};

const durationMins = (startAt, endAt) => {
  if (!startAt || !endAt) return null;
  const s = parseAppDate(startAt); const e = parseAppDate(endAt);
  if (!s || !e) return null;
  const m = Math.round((e - s) / 60000);
  return m > 0 ? m : null;
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const Label = ({ children }) => (
  <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-muted-foreground">{children}</p>
);

const PersonCard = ({ p, isHost }) => (
  <div className="group relative rounded-2xl border border-border bg-card overflow-hidden hover:shadow-elevated hover:border-foreground/30 transition-all" data-testid={isHost ? 'speaker-host' : 'speaker-guest'}>
    <div className="relative aspect-square overflow-hidden bg-secondary">
      {p.avatar
        ? <img src={p.avatar} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        : <div className="w-full h-full grid place-items-center text-3xl font-display text-muted-foreground">{(p.name?.[0] || '?').toUpperCase()}</div>}
      {isHost && <span className="absolute top-2.5 left-2.5 text-[10px] uppercase tracking-wider font-bold text-white bg-foreground/80 backdrop-blur px-2 py-0.5 rounded-full">Host</span>}
      {(p.bio || p.linkedin) && (
        <div className="absolute inset-0 bg-foreground/92 text-background p-4 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-testid="speaker-hover">
          {p.bio && <p className="text-xs leading-relaxed line-clamp-6">{p.bio}</p>}
          {p.linkedin && (
            <a href={p.linkedin} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-background hover:underline" data-testid="speaker-linkedin">
              <Linkedin className="h-3.5 w-3.5" /> View LinkedIn
            </a>
          )}
        </div>
      )}
    </div>
    <div className="p-3">
      <p className="text-sm font-bold text-foreground truncate">{p.name}</p>
      {p.role && <p className="text-xs text-muted-foreground truncate mt-0.5">{p.role}</p>}
    </div>
  </div>
);

const StatCard = ({ label, value, variant }) => {
  const styles = {
    blue: 'bg-blue-500 text-white',
    purple: 'bg-violet-500 text-white',
    dark: 'bg-zinc-900 text-white',
  }[variant];
  return (
    <div className={`rounded-2xl p-4 ${styles}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-70">{label}</p>
      <p className="text-xl font-bold mt-1.5 leading-tight">{value}</p>
    </div>
  );
};

const EventDetail = () => {
  const { eventId } = useParams();
  const api = useApi();
  const { item: event, loading } = useDirectoryItem('events', eventId);
  const [rsvp, setRsvp] = useState(null); // {going, checked_in, can_check_in, registered}
  const [busy, setBusy] = useState(false);
  const [fit, setFit] = useState(null);

  useEffect(() => {
    if (!eventId) return;
    api.get(`/api/events/${eventId}/rsvp/me`).then(setRsvp).catch(() => {});
    api.get(`/api/event/${eventId}/fit-score`).then(setFit).catch(() => setFit(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const [formOpen, setFormOpen] = useState(false);
  const [answers, setAnswers] = useState({});

  const doRsvp = async (withAnswers = null) => {
    const fields = event?.rsvp_form || [];
    if (fields.length && withAnswers === null) { setFormOpen(true); return; }
    setBusy(true);
    try {
      const r = await api.post(`/api/events/${eventId}/rsvp`, { answers: withAnswers || {} });
      setRsvp((p) => ({ ...p, going: r.status === 'going', waitlisted: r.status === 'waitlisted', status: r.status, registered: r.registered }));
      setFormOpen(false);
      toast.success(r.waitlisted ? "You're on the waitlist" : "You're going!", { description: r.waitlisted ? "We'll notify you if a seat opens up." : undefined });
    } catch (e) { toast.error('Could not RSVP', { description: e.message }); }
    finally { setBusy(false); }
  };
  const cancelRsvp = async () => {
    setBusy(true);
    try { const r = await api.del(`/api/events/${eventId}/rsvp`); setRsvp((p) => ({ ...p, going: false, waitlisted: false, status: null, checked_in: false, registered: r.registered })); toast('RSVP cancelled'); }
    catch (e) { toast.error('Could not cancel', { description: e.message }); }
    finally { setBusy(false); }
  };
  const checkIn = async () => {
    setBusy(true);
    try { await api.post(`/api/events/${eventId}/checkin`); setRsvp((p) => ({ ...p, checked_in: true })); toast.success('Checked in ✓'); }
    catch (e) { toast.error('Check-in failed', { description: e.message }); }
    finally { setBusy(false); }
  };

  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  if (loading) return <DetailSkeleton />;
  if (!event) return <DetailNotFound backTo="/app/events" label="Back to Events" />;

  const when = formatEventWhen(event.start_at, event.end_at, event.date) || 'Date to be announced';
  const guests = Array.isArray(event.guests) ? event.guests.filter((g) => g?.name) : [];
  const agenda = Array.isArray(event.agenda) ? event.agenda.filter((a) => a?.title) : [];
  const questions = Array.isArray(event.copilot_questions) ? event.copilot_questions.filter(Boolean) : [];
  const maps = mapsUrl(event);
  const isOnline = event.mode === 'Online';
  const isHybrid = event.mode === 'Hybrid';
  const hasVirtualAccess = isOnline || isHybrid;
  const isDailyEvent = event.meeting_provider === 'daily';
  const hasJoinTarget = !!event.meeting_link || isDailyEvent;
  const locName = event.location_name || event.location;
  const locLine = [event.location_address, event.location_city].filter(Boolean).join(', ');
  const badge = dayBadge(event.start_at);
  const cap = Number(event.capacity) || 0;
  const registered = (rsvp && typeof rsvp.registered === 'number') ? rsvp.registered : (Number(event.registered) || 0);
  const pct = cap ? Math.min(100, Math.round((registered / cap) * 100)) : 0;
  const totalMins = durationMins(event.start_at, event.end_at);
  const closingText = (() => {
    const c = event.closing_in;
    if (!c) return '';
    if (c === 'Closed') return ' Registration closed.';
    if (c === 'Today') return ' Closing today.';
    return ` Closing in ${c}.`;
  })();
  const host = event.host ? { name: event.host, role: event.host_role || (event.kind ? `${event.kind} host` : ''), avatar: event.host_avatar, bio: event.host_bio, linkedin: event.host_linkedin } : null;
  const mapQuery = [event.location_name, event.location_address, event.location_city].filter(Boolean).join(', ') || event.location || '';
  const mapEmbed = (!isOnline && mapQuery) ? `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=15&output=embed` : null;
  const publicUrl = typeof window !== 'undefined' ? `${window.location.origin}/api/share/e/${eventId}` : '';
  const soldOut = rsvp?.sold_out && !rsvp?.going && !rsvp?.waitlisted;
  const eventPast = !!(rsvp?.past || event?.past);
  const isEventHost = !!rsvp?.is_host;
  const evStartMs = event.start_at ? (parseAppDate(event.start_at)?.getTime() || 0) : 0;
  const evEndMs = effectiveEndMs(event.start_at, event.end_at, 2 * 3600 * 1000) || 0;
  const joinOpen = hasVirtualAccess && (!!rsvp?.going || isEventHost) && hasJoinTarget && !eventPast
    && !!evStartMs && nowTs >= evStartMs - 15 * 60 * 1000 && nowTs <= evEndMs;
  const waitlistN = rsvp?.waitlist || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="event-detail">
      <Link to="/app/events" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="event-back">
        <ArrowLeft className="h-4 w-4" /> Back to Events
      </Link>

      {/* Cover image (16:9, contained so poster content is never cropped)
          + separate content block below. Separating the two guarantees the
          uploaded poster is fully readable — no title text overlapping. */}
      <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0} className="rounded-3xl overflow-hidden bg-zinc-950">
        <div className="relative w-full aspect-video bg-zinc-950" data-testid="event-cover-wrap">
          <img
            src={event.cover_image || DEFAULT_COVER}
            alt={event.title}
            className="absolute inset-0 w-full h-full object-contain"
            data-testid="event-cover"
          />
        </div>
        <div className="p-6 sm:p-10 bg-zinc-900 text-white">
          <div className="flex flex-wrap gap-2 mb-5">
            {badge && <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${badge.live ? 'bg-red-500 text-white' : 'bg-white/15 text-white backdrop-blur'}`} data-testid="event-day-badge">{badge.live && <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />}{badge.label}</span>}
            {event.kind && <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/15 text-white backdrop-blur" data-testid="event-kind">{event.kind}</span>}
            {event.audience === 'internal' && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-400/20 text-amber-200 backdrop-blur" data-testid="event-internal-badge"><Lock className="h-3 w-3" /> Invite only</span>}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.98] tracking-tight max-w-4xl">{event.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-7 gap-y-2 text-sm text-white/85 font-medium">
            <span className="inline-flex items-center gap-2"><Calendar className="h-4 w-4 opacity-70" /> {when}</span>
            {(locName || isOnline) && <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 opacity-70" /> {isOnline ? 'Online' : locName}</span>}
            <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 opacity-70" /> {cap ? `${registered} / ${cap} registered` : 'Open to all'}</span>
          </div>
        </div>
      </motion.div>

      {/* Body grid — on mobile the reserve panel jumps ABOVE the content so
          RSVP is one thumb-tap away from the hero; on lg+ it returns to the
          right sidebar via `order` reset. */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
        <main className="order-2 lg:order-none lg:col-span-8 space-y-6 min-w-0">
          {/* About */}
          <motion.section variants={fadeUp} initial="hidden" animate="show" custom={1} className="rounded-3xl border border-border bg-card p-6 sm:p-8">
            <Label>About this event</Label>
            <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">{event.takeaway_heading || "What you'll take away."}</h2>
            {event.description
              ? <RichHtml value={event.description} className="mt-4" testid="event-description" />
              : <p className="text-muted-foreground mt-4 text-base leading-relaxed" data-testid="event-description">No description has been added for this event yet.</p>}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
              <StatCard label="Event type" value={event.kind || 'Event'} variant="blue" />
              <StatCard label="Format" value={event.mode || 'TBD'} variant="purple" />
              <StatCard label="Capacity" value={cap || '∞'} variant="dark" />
            </div>
          </motion.section>

          {/* Agenda */}
          {agenda.length > 0 && (
            <motion.section variants={fadeUp} initial="hidden" animate="show" custom={2} className="rounded-3xl border border-border bg-card p-6 sm:p-8" data-testid="event-agenda">
              <div className="flex items-start justify-between">
                <div>
                  <Label>Agenda</Label>
                  <h2 className="font-display text-2xl sm:text-3xl mt-2 tracking-tight">{totalMins ? `${totalMins} minutes` : 'Schedule'} · {agenda.length} block{agenda.length > 1 ? 's' : ''}</h2>
                </div>
                <Mic className="h-5 w-5 text-muted-foreground shrink-0" />
              </div>
              <ol className="relative border-l border-border ml-1.5 mt-7 space-y-7">
                {agenda.map((a, i) => (
                  <li key={i} className="relative pl-7 flex items-start justify-between gap-4" data-testid={`agenda-row-${i}`}>
                    <span className={`absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full ring-4 ring-card ${i === 0 ? 'bg-primary' : 'bg-foreground'}`} />
                    <div className="min-w-0">
                      {a.time && <p className="text-sm font-mono text-muted-foreground">{a.time}</p>}
                      <p className="text-base font-semibold text-foreground mt-0.5">{a.title}</p>
                    </div>
                    {a.duration && <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground shrink-0 pt-1">{a.duration}</span>}
                  </li>
                ))}
              </ol>
            </motion.section>
          )}

          {/* Speakers & hosts — photo, intro, LinkedIn */}
          {(host || guests.length > 0) && (
            <motion.section variants={fadeUp} initial="hidden" animate="show" custom={3} className="rounded-3xl border border-border bg-card p-6 sm:p-8" data-testid="event-speakers">
              <Label>Speakers &amp; hosts</Label>
              <p className="text-xs text-muted-foreground mt-1">Hover a card for their intro &amp; LinkedIn.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-5">
                {host && <PersonCard p={host} isHost />}
                {guests.map((g, i) => <PersonCard key={i} p={g} />)}
              </div>
            </motion.section>
          )}

          {/* Copilot prep */}
          {questions.length > 0 && (
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4}>
              <CopilotPrepCard
                heading={`${questions.length} questions to ask ${event.host || 'the host'}:`}
                items={questions}
                testId="event-copilot-prep"
              />
            </motion.div>
          )}
        </main>

        {/* Sticky reserve rail — order-1 on mobile so it sits between the
            hero title block and the About section per the mobile-web spec. */}
        <aside className="order-1 lg:order-none lg:col-span-4">
          {fit && <div className="mb-4"><FitScoreCard fit={fit} title="Copilot fit for you" testId="event-fit-card" /></div>}
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="lg:sticky lg:top-8 rounded-3xl border border-border bg-card p-6 sm:p-7 shadow-sm">
            <Label>Reserve your seat</Label>
            <p className="font-display text-3xl font-bold mt-2">{event.price ? event.price : "It's free."}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {cap ? `${registered} / ${cap} seats taken.` : 'Open to all Bower members.'}
              {closingText}
              {waitlistN > 0 ? ` · ${waitlistN} on waitlist` : ''}
            </p>
            {cap > 0 && (
              <div className="mt-4 h-2 w-full rounded-full bg-secondary overflow-hidden" data-testid="seats-progress">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
              </div>
            )}

            <div className="mt-5 flex flex-col gap-3">
              {eventPast && isDailyEvent ? (
                <Button asChild className="w-full h-12 rounded-xl font-bold gap-2" data-testid="event-view-recording">
                  <Link to={`/app/meet/event/${event.id}/recordings`}><Video className="h-4 w-4" /> View recording & AI summary</Link>
                </Button>
              ) : isEventHost && !eventPast ? (
                <>
                  {joinOpen ? (
                    <Button asChild className="w-full h-12 rounded-xl font-bold gap-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 active:scale-[0.98] transition-all" data-testid="event-host-join">
                      {isDailyEvent ? (
                        <Link to={`/app/meet/event/${event.id}`}><span className="h-2 w-2 rounded-full bg-white live-dot" /> Start as host — Live</Link>
                      ) : (
                        <a href={event.meeting_link} target="_blank" rel="noopener noreferrer"><span className="h-2 w-2 rounded-full bg-white live-dot" /> Join now — Live</a>
                      )}
                    </Button>
                  ) : (
                    <div className="flex items-center justify-center gap-2 h-12 rounded-xl bg-primary/10 text-primary font-bold border border-primary/20" data-testid="event-hosting-badge">
                      <Mic className="h-5 w-5" /> You're hosting this event
                    </div>
                  )}
                  {!joinOpen && hasJoinTarget && <JoinCountdownNote start={event.start_at} />}
                </>
              ) : rsvp?.waitlisted ? (
                <>
                  <div className="flex items-center justify-center gap-2 h-12 rounded-xl bg-amber-500/10 text-amber-600 font-bold border border-amber-500/20" data-testid="rsvp-waitlisted">
                    <Clock className="h-5 w-5" /> You're on the waitlist
                  </div>
                  <Button variant="ghost" onClick={cancelRsvp} disabled={busy} className="w-full h-9 rounded-xl text-muted-foreground hover:text-destructive text-sm" data-testid="event-cancel-rsvp">Leave waitlist</Button>
                </>
              ) : rsvp?.going ? (
                <>
                  {joinOpen ? (
                    <Button asChild className="w-full h-12 rounded-xl font-bold gap-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 active:scale-[0.98] transition-all" data-testid="event-join-live">
                      {isDailyEvent ? (
                        <Link to={`/app/meet/event/${event.id}`}><span className="h-2 w-2 rounded-full bg-white live-dot" /> Join now — Live</Link>
                      ) : (
                        <a href={event.meeting_link} target="_blank" rel="noopener noreferrer"><span className="h-2 w-2 rounded-full bg-white live-dot" /> Join now — Live</a>
                      )}
                    </Button>
                  ) : (
                    <div className="flex items-center justify-center gap-2 h-12 rounded-xl bg-success/10 text-success font-bold border border-success/20" data-testid="rsvp-confirmed">
                      <CheckCircle2 className="h-5 w-5" /> {rsvp.checked_in ? "You're checked in" : "You're going"}
                    </div>
                  )}
                  {!joinOpen && hasVirtualAccess && hasJoinTarget && !eventPast && <JoinCountdownNote start={event.start_at} />}
                  {rsvp.can_check_in && !rsvp.checked_in && !isDailyEvent && (
                    <Button onClick={checkIn} disabled={busy} className="w-full h-11 rounded-xl gap-2 font-bold active:scale-[0.98] transition-all" data-testid="event-self-checkin">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />} Check in now
                    </Button>
                  )}
                  <Button variant="ghost" onClick={cancelRsvp} disabled={busy} className="w-full h-9 rounded-xl text-muted-foreground hover:text-destructive text-sm" data-testid="event-cancel-rsvp">
                    Cancel my RSVP
                  </Button>
                </>
              ) : eventPast ? (
                <Button disabled className="w-full h-12 rounded-xl font-bold gap-2 bg-muted text-muted-foreground cursor-not-allowed" data-testid="rsvp-submit-button">
                  <Clock className="h-4 w-4" /> Registration closed
                </Button>
              ) : (
                <Button onClick={() => doRsvp()} disabled={busy} className={`w-full h-12 rounded-xl font-bold gap-2 active:scale-[0.98] transition-all ${soldOut ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-foreground text-background hover:bg-foreground/90'}`} data-testid="rsvp-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : soldOut ? <><Clock className="h-4 w-4" /> Join the waitlist</> : <>RSVP now <ArrowUpRight className="h-4 w-4" /></>}
                </Button>
              )}
              {soldOut && !rsvp?.waitlisted && <p className="text-xs text-amber-600 text-center -mt-1" data-testid="sold-out-note">This event is full — join the waitlist and we'll notify you.</p>}
              {hasVirtualAccess && !rsvp?.going && !isEventHost && !eventPast && (
                <p className="text-xs text-muted-foreground text-center -mt-1" data-testid="event-join-locked-note">
                  Join link unlocks after you RSVP.
                </p>
              )}
              <Button variant="ghost" className="w-full h-10 rounded-xl gap-2 text-muted-foreground hover:text-foreground" data-testid="event-notify-button">
                <Bell className="h-4 w-4" /> Notify me 1 hour before
              </Button>
            </div>

            {/* Prominent share / invite CTA */}
            <div className="mt-5 pt-5 border-t border-border" data-testid="event-share-cta">
              <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2.5">Invite others</p>
              <ShareEvent
                title={event.title}
                url={publicUrl}
                embed={{
                  type: 'event',
                  ref_id: event.id,
                  title: event.title,
                  subtitle: (event.description || '').replace(/<[^>]+>/g, '').slice(0, 160),
                  org: event.host,
                  cover_url: event.cover_image,
                  url: `/app/events/${event.id}`,
                  meta: { start_at: event.start_at, end_at: event.end_at, location: locName || event.location_city, vertical: event.vertical },
                }}
              />
            </div>

            {/* Location row */}
            {!isOnline && locName && (
              <div className="mt-6 pt-5 border-t border-border">
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium break-words">{locName}</p>
                    {locLine && <p className="text-xs text-muted-foreground break-words">{locLine}</p>}
                    {maps && <a href={maps} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-foreground text-xs font-medium mt-1 underline underline-offset-4" data-testid="event-directions"><Navigation className="h-3 w-3" /> View directions</a>}
                  </div>
                </div>
                {mapEmbed && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-border" data-testid="event-map">
                    <iframe title="Event location map" src={mapEmbed} className="w-full h-40 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                  </div>
                )}
              </div>
            )}

            {/* Hosted by */}
            {host && (
              <div className="mt-6 pt-5 border-t border-border" data-testid="event-host-card">
                <Label>Hosted by</Label>
                <div className="mt-3 flex items-center gap-3">
                  <Avatar className="h-11 w-11"><AvatarImage src={host.avatar} className="object-cover" /><AvatarFallback>{(host.name[0] || '?').toUpperCase()}</AvatarFallback></Avatar>
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">{host.name}</p>
                    {host.role && <p className="text-xs text-muted-foreground truncate">{host.role}</p>}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </aside>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="rsvp-form-dialog" onClick={() => setFormOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-card border border-border p-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div>
              <p className="font-display text-xl">A few quick details</p>
              <p className="text-xs text-muted-foreground mt-1">The organiser needs this to confirm your spot.</p>
            </div>
            {(event.rsvp_form || []).map((f) => (
              <div key={f.id} data-testid={`rsvp-form-field-${f.id}`}>
                {f.type === 'checkbox' ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!answers[f.id]} onChange={(e) => setAnswers((p) => ({ ...p, [f.id]: e.target.checked }))} className="h-4 w-4 accent-primary" data-testid={`rsvp-form-input-${f.id}`} />
                    {f.label}{f.required && <span className="text-destructive">*</span>}
                  </label>
                ) : (
                  <>
                    <label className="text-xs font-medium text-muted-foreground">{f.label}{f.required && <span className="text-destructive"> *</span>}</label>
                    {f.type === 'textarea' ? (
                      <textarea rows={3} value={answers[f.id] || ''} onChange={(e) => setAnswers((p) => ({ ...p, [f.id]: e.target.value }))} className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid={`rsvp-form-input-${f.id}`} />
                    ) : f.type === 'select' ? (
                      <select value={answers[f.id] || ''} onChange={(e) => setAnswers((p) => ({ ...p, [f.id]: e.target.value }))} className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid={`rsvp-form-input-${f.id}`}>
                        <option value="">Select…</option>
                        {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type === 'number' ? 'number' : 'text'} value={answers[f.id] || ''} onChange={(e) => setAnswers((p) => ({ ...p, [f.id]: e.target.value }))} className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid={`rsvp-form-input-${f.id}`} />
                    )}
                  </>
                )}
              </div>
            ))}
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="outline" className="rounded-full" onClick={() => setFormOpen(false)} data-testid="rsvp-form-cancel">Cancel</Button>
              <Button className="rounded-full" disabled={busy} onClick={() => {
                const missing = (event.rsvp_form || []).filter((f) => f.required && (f.type === 'checkbox' ? !answers[f.id] : !String(answers[f.id] ?? '').trim()));
                if (missing.length) { toast.error(`Please fill in: ${missing.map((m) => m.label).join(', ')}`); return; }
                doRsvp(answers);
              }} data-testid="rsvp-form-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm RSVP'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
